import { faShieldCheck as lightShieldCheck } from "@jewlr/pro-light-svg-icons/faShieldCheck"
import { faMinus } from "@jewlr/pro-regular-svg-icons/faMinus"
import { faPlus } from "@jewlr/pro-regular-svg-icons/faPlus"
import { faChevronDown } from "@jewlr/pro-solid-svg-icons/faChevronDown"
import { faChevronUp } from "@jewlr/pro-solid-svg-icons/faChevronUp"
import PropTypes from "prop-types"
import { useCallback, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"

import { setWarranty } from "areas/cart/store/actions"
import Button from "components/ui/button"
import Checkbox from "components/ui/checkbox"
import Expand from "components/ui/expand"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import Media from "components/ui/media"
import { jewlr_t, isTestMode } from "helpers/application"
import { to_currency } from "helpers/number"
import { cn } from "styles/utils"

import WarrantyPlans from "./warranty-plans"

const Heading = ({ ...props }) => (
  <p
    className="text-primary text-16 tracking-tigher leading-20 font-medium"
    {...props}
  />
)
const Desc = ({ className = "", ...props }) => (
  <p
    className={cn(
      "text-13 tablet:text-12 tablet:font-light tracking-tigher leading-16 tablet:leading-20 font-medium",
      className
    )}
    {...props}
  />
)

const planItems = [
  {
    img: "fragile.svg",
    key: "accident-protection",
    title: (
      <>
        Accident
        <br />
        Protection
      </>
    ),
  },
  {
    img: "user-check.svg",
    key: "fast-replacements-repairs",
    title: (
      <>
        Fast Replacements
        <br /> + Repairs
      </>
    ),
  },
  {
    img: "shield.svg",
    key: "easy-online-claims",
    title: (
      <>
        Easy Online
        <br />
        Claims
      </>
    ),
  },
]

const WarrantyAddon = ({ item }) => {
  if (!item.warranty_plans || item.warranty_plans.length === 0) return ""

  const itemWarranty = item.addons.find((a) => a.addon_type === "WARRANTY")

  const [showWarranty, setShowWarranty] = useState(false)
  const [addWarrantyPlan, setAddWarrantyPlan] = useState(!!itemWarranty)

  const cartWarrantyExp = useSelector(
    (state) => state.experiment.cartWarranty.variant_id
  )

  const dispatch = useDispatch()

  const handleAddWarrantyPlanChange = useCallback(
    (isAdd) => {
      setAddWarrantyPlan(isAdd)

      if (isAdd) {
        const bestSellerPlan = item.warranty_plans.find(
          (plan) => plan.label === "Best Seller"
        )
        const warrantyPlan = bestSellerPlan || item.warranty_plans?.[0]

        if (warrantyPlan) {
          if (!isTestMode) {
            dataLayer.push({
              event: "added-warranty-to-cart",
              warrantyPlan: warrantyPlan.id,
            })
          }
          dispatch(
            setWarranty(item.id, bestSellerPlan || item.warranty_plans?.[0])
          )
        }
      } else {
        dispatch(setWarranty(item.id, {}))
      }
    },
    [dispatch, setAddWarrantyPlan, item]
  )

  return cartWarrantyExp === 1 ? (
    <div className="bg-grey-4 mt-10 px-12 py-16" data-cy="warranty-addon">
      <Checkbox
        aria-checked={addWarrantyPlan}
        aria-controls={`warranty-accordion-content-${item.id}`}
        aria-labelledby={`warranty-heading-${item.id}`}
        checkboxClassName="border-primary text-white aria-[checked=true]:bg-primary"
        checked={addWarrantyPlan}
        className="w-full items-start"
        data-cy={`cart-line-item-protection-checkbox-${item.style_code}`}
        id={`warranty-accordion-heading-${item.id}`}
        onChange={() => {
          if (!isTestMode) {
            dataLayer.push({ event: "expand-cart-line-item-warranty-option" })
          }
          handleAddWarrantyPlanChange(!addWarrantyPlan)
        }}
        role="switch"
      >
        <div className="w-full">
          <div className="leading-16 mb-6 flex items-baseline justify-between gap-4">
            <h2
              className="text-primary text-14 mobile:text-16 font-bold"
              id={`warranty-heading-${item.id}`}
            >
              Add Protection Plan
            </h2>
            <span className="text-grey-65 text-10 mobile:text-12 grow-0 text-right">
              Powered by
              <Image
                alt="Extend"
                className="ml-4 inline-block w-auto align-baseline"
                height="16"
                src={require("images/checkout/extend.svg")}
                style={{ height: "12px" }}
                width="64"
              />
            </span>
          </div>
          <p className="text-13">
            Select a plan to protect your {jewlr_t("terms.jewelry")} from
            accidental damage.
          </p>
        </div>
      </Checkbox>
      {item.warranty_plans?.length > 0 && (
        <Expand
          aria-labelledby={`warranty-accordion-heading-${item.id}`}
          className="pl-6 pt-10"
          id={`warranty-accordion-content-${item.id}`}
          role="region"
          show={addWarrantyPlan}
        >
          <div aria-label="Select a protection plan" role="radiogroup">
            {item.warranty_plans.map((plan) => {
              const selected = plan.value === itemWarranty?.addon_code

              return (
                <label
                  className="my-10 flex cursor-pointer items-center"
                  htmlFor={`warranty-${item.id}-${plan.id}`}
                  key={`warranty-${item.id}-${plan.id}`}
                >
                  <input
                    checked={selected}
                    className="sr-only"
                    data-cy={`protection-plan-${plan.duration}`}
                    id={`warranty-${item.id}-${plan.id}`}
                    onChange={() => {
                      if (!isTestMode) {
                        dataLayer.push({
                          event: "added-warranty-to-cart",
                          warrantyPlan: plan.id,
                        })
                      }
                      dispatch(setWarranty(item.id, plan))
                    }}
                    type="radio"
                  />
                  <div
                    aria-hidden
                    className={cn(
                      "h-(--base-width) w-(--base-width) before:z-2 after:h-(--after-width) after:w-(--after-width) after:z-1 before:h-(--before-height) before:w-(--before-width) relative mr-6 before:absolute before:left-6 before:top-3 before:rotate-45 before:border-b-2 before:border-r-2 before:border-b-white before:border-r-white after:absolute after:rounded-full after:border after:border-black",
                      selected
                        ? "after:bg-primary after:border-primary before:block"
                        : "before:hidden after:bg-white"
                    )}
                    style={{
                      "--after-width": "17px",
                      "--base-width": "18px",
                      "--before-height": "9px",
                      "--before-width": "5px",
                    }}
                  />
                  <div className="flex w-full items-center">
                    <h3
                      className="text-12 mobile:text-14 desktop:text-16"
                      data-cy="warranty-addon-plan-price"
                    >
                      {plan.duration.toLowerCase()} protection plan &ndash;{" "}
                      {to_currency(plan.price, { precision: 2 })}
                    </h3>
                    {plan.label && (
                      <span className="bg-primary h-22 text-10 mobile:text-12 mobile:px-8 mobile:ml-16 ml-8 inline-flex shrink-0 grow-0 items-center justify-center rounded-sm px-4 text-white">
                        {plan.label}
                      </span>
                    )}
                  </div>
                </label>
              )
            })}
          </div>
          <div className="mt-24 bg-white py-10">
            <div className="grid grid-cols-3 text-center">
              {planItems.map((item) => (
                <div key={`warranty-perks-${item.key}`}>
                  <Image
                    alt=""
                    className="mb-8"
                    height="17"
                    src={require(`images/cart/warranty/${item.img}`)}
                    width="18"
                  />
                  <p className="text-10 mobile:text-12 desktop:text-14 font-medium">
                    {item.title}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex justify-center">
              <Button
                asChild
                className="text-12 mt-14 font-medium"
                text
                underline
                variant="secondary"
              >
                <Link
                  rel="nofollow noreferrer"
                  target="_blank"
                  to="https://customers.extend.com/en-US/plan_details/A0-AAJEW-2y"
                >
                  Learn more about the plans
                </Link>
              </Button>
            </div>
          </div>
        </Expand>
      )}
    </div>
  ) : (
    <div
      className="border-t-grey-10 tablet:px-4 py-18 tablet:py-14 border-t"
      data-cy="warranty-addon"
    >
      <h3>
        <Button
          aria-controls={`cart-line-item-warranty-addon-content-${item.id}`}
          aria-expanded={showWarranty}
          aria-label="Add protection plan"
          className="block w-full"
          data-cy={`cart-line-item-protection-checkbox-${item.style_code}`}
          id={`cart-line-item-warranty-addon-heading-${item.id}`}
          onClick={() => {
            setShowWarranty(!showWarranty)
            if (typeof FS !== "undefined") {
              FS.event("Extend Cart Offers Toggled")
            }
            if (!isTestMode) {
              dataLayer.push({ event: "expand-cart-line-item-warranty-option" })
            }
          }}
        >
          <div className="flex items-baseline justify-between">
            <div className="flex">
              <FontAwesomeIcon
                className="text-primary mr-8 mt-2"
                icon={lightShieldCheck}
              />
              <Media className="text-left" lessThan="tablet">
                <Heading>Add Protection Plan</Heading>
                <Desc className="mt-10">
                  Select a plan to protect your {jewlr_t("terms.jewelry")} from
                  accidental damage.
                </Desc>
              </Media>
              <Media className="text-left" greaterThanOrEqual="tablet">
                <Heading>Protect Your {jewlr_t("terms.Jewelry")}</Heading>
                <Desc>
                  Select a plan to protect your {jewlr_t("terms.jewelry")} from
                  accidental damage.
                </Desc>
              </Media>
            </div>
            <Media lessThan="tablet">
              <div className="flex">
                <FontAwesomeIcon
                  className="text-primary text-14"
                  icon={showWarranty ? faMinus : faPlus}
                />
              </div>
            </Media>
            <Media greaterThanOrEqual="tablet">
              <div className="flex items-center">
                <p className="text-primary text-12 tracking-tigher leading-14 mr-6 font-medium">
                  Add Protection Plan
                </p>
                <FontAwesomeIcon
                  className="text-primary text-9"
                  icon={showWarranty ? faChevronUp : faChevronDown}
                />
              </div>
            </Media>
          </div>
        </Button>
      </h3>
      <Expand
        aria-labelledby={`cart-line-item-warranty-addon-content-${item.id}`}
        id={`cart-line-item-warranty-addon-heading-${item.id}`}
        show={showWarranty}
      >
        <div className="tablet:ml-24 tablet:mt-16 mt-10">
          <WarrantyPlans item={item} />
        </div>
      </Expand>
    </div>
  )
}

WarrantyAddon.propTypes = {
  item: PropTypes.object,
}

Desc.propTypes = {
  className: PropTypes.string,
}

export default WarrantyAddon
